import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import "./MobileDevelopment.css";

const CONTACT = "/contact?service=Mobile%20App%20Development";

const builds = [
  ["01", "briefcase", "Business Apps", "Tools built around internal workflows."],
  ["02", "user", "Customer Apps", "Mobile experiences designed for customers."],
  ["03", "calendar", "Booking Apps", "Appointments, scheduling, and customer actions."],
  ["04", "bag", "E-Commerce Apps", "Product discovery, carts, accounts, and shopping."],
  ["05", "pin", "Service Apps", "Connect customers with the services they need."],
  ["06", "chart", "Dashboard Apps", "The numbers and tasks a team needs while away from a desk."],
  ["07", "tool", "Internal Tools", "Mobile workflows for teams and operations."],
  ["08", "phone", "Mobile Experiences", "The screens, navigation, and actions people use on a device."]
];

const journey = [
  ["discover", "Discover", "Find the product."],
  ["open", "Open", "Land on a clear first screen."],
  ["explore", "Explore", "Move through the app by touch."],
  ["action", "Take Action", "Book, buy, or complete a task."],
  ["return", "Return", "Come back through a notification or habit."]
];

const focusPoints = [
  ["nav", "Navigation", "A small set of places, easy to reach with a thumb."],
  ["touch", "Touch", "Controls sized for a finger, not a cursor."],
  ["content", "Content", "The next piece of information, close to the action."],
  ["actions", "Actions", "One clear thing to do on the screen."],
  ["notes", "Notifications", "A reason to return, when the product needs it."]
];

const platforms = [
  ["ios", "iOS", "The same product, laid out for iPhone."],
  ["android", "Android", "The same product, laid out for Android."],
  ["cross", "Cross-platform", "One product experience shared across both."]
];

const hubNodes = [
  ["api", "API", "50%", "10%"],
  ["auth", "Authentication", "16%", "30%"],
  ["data", "Database", "84%", "30%"],
  ["note", "Notifications", "12%", "68%"],
  ["pay", "Payments", "88%", "64%"],
  ["stats", "Analytics", "30%", "88%"],
  ["ops", "Business systems", "72%", "86%"]
];

const hubMobile = [
  ["api", 50, 9],
  ["auth", 20, 26],
  ["data", 80, 26],
  ["note", 18, 64],
  ["pay", 82, 64],
  ["stats", 28, 88],
  ["ops", 72, 88]
];

const process = [
  ["Discover", "Understand the product, users, goals, and requirements."],
  ["Plan", "Define the app structure, features, user flows, and technical requirements."],
  ["Design", "Create the interface, interactions, and overall mobile experience."],
  ["Build", "Develop the app, backend functionality, and required integrations."],
  ["Test", "Test devices, screen sizes, interactions, functionality, and performance."],
  ["Launch", "Prepare the application for release and production use."],
  ["Improve", "Continue refining the product based on real usage and business needs."]
];

const principles = [
  ["User First", "Start with the people who will actually use the product."],
  ["Purposeful Features", "Focus on functionality that supports the product's goals."],
  ["Connected Systems", "Build the app with the backend, APIs, and business systems in mind."],
  ["Ready to Evolve", "Create a foundation that can improve as the product grows."]
];

const faqs = [
  ["What types of mobile apps can you build?", "Business, customer, booking, service, commerce, and internal tools. The feature set follows the product and the people using it."],
  ["Can you build for both iOS and Android?", "Yes. A product can be built for one platform or both, depending on where the users are. That choice is made during planning."],
  ["Can you work with an existing app?", "Yes, when the current codebase and accounts can support the change. We review what should stay, what should be rebuilt, and what is safer to replace."],
  ["Can the app connect to an existing website or backend?", "Yes, where that system offers an API or another supported connection. The app can share data with a website or backend the business already runs."],
  ["Can you integrate APIs and third-party services?", "Yes, where the other service provides an API or a supported connection. Typical cases are authentication, notifications, payments, and analytics. What is possible depends on that service."],
  ["Can you help design the app interface?", "Yes. Interface, navigation, and the main flows are part of the product work, alongside the build."],
  ["Can you support the app after launch?", "Yes. Support can cover fixes, updates, and later changes. That scope is agreed separately from the initial release."],
  ["Can you build a cross-platform application?", "Yes, when one codebase is the right fit. Native work is still an option when a platform needs it. The choice follows the product, not a fixed stack."]
];

function useInView(threshold = 0.22) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShown(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShown(true);
        observer.disconnect();
      }
    }, { threshold, rootMargin: "0px 0px -8% 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, shown];
}

function Reveal({ children, className = "", delay = 0 }) {
  const [ref, shown] = useInView();
  return (
    <div ref={ref} className={`ma-reveal ${shown ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function Kicker({ index, label }) {
  return (
    <p className="ma-kicker">
      <span>{index}</span>
      <span>{label}</span>
    </p>
  );
}

function Glyph({ type }) {
  const common = { width: 15, height: 15, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: 1.4, "aria-hidden": true };
  if (type === "briefcase") return <svg {...common}><rect x="2.5" y="5" width="11" height="8" rx="1" /><path d="M6 5V3.5h4V5" /></svg>;
  if (type === "user") return <svg {...common}><circle cx="8" cy="6" r="2.2" /><path d="M3.5 13c.8-2 2.4-3 4.5-3s3.7 1 4.5 3" /></svg>;
  if (type === "calendar") return <svg {...common}><rect x="2.5" y="3.5" width="11" height="10" rx="1" /><path d="M2.5 6.5h11M5.5 2.5v2M10.5 2.5v2" /></svg>;
  if (type === "bag") return <svg {...common}><path d="M4 5h8l-.6 8H4.6L4 5z" /><path d="M6 5V4a2 2 0 0 1 4 0v1" /></svg>;
  if (type === "pin") return <svg {...common}><path d="M8 14s4-3.4 4-6.2A4 4 0 0 0 4 7.8C4 10.6 8 14 8 14z" /><circle cx="8" cy="7.5" r="1.2" /></svg>;
  if (type === "chart") return <svg {...common}><path d="M3 13V8M7 13V5M11 13V3" /></svg>;
  if (type === "tool") return <svg {...common}><path d="M9 3.5l3.5 3.5-6 6L3 9.5l6-6z" /><path d="M8 4.5l3 3" /></svg>;
  return <svg {...common}><rect x="5" y="2" width="6" height="12" rx="1.2" /><path d="M7 12.2h2" /></svg>;
}

function Screen({ kind }) {
  if (kind === "detail") {
    return (
      <div className="ma-screen is-detail">
        <p>Session</p>
        <strong>Studio hour</strong>
        <div className="ma-shot" />
        <span>Thu · 2:00</span>
        <em>45 min</em>
        <button type="button">Reserve</button>
      </div>
    );
  }
  if (kind === "account") {
    return (
      <div className="ma-screen">
        <p>Account</p>
        <div className="ma-person"><i /><b>Alex Morgan</b></div>
        <ul>
          <li>Upcoming</li>
          <li>History</li>
          <li>Settings</li>
        </ul>
      </div>
    );
  }
  if (kind === "book") {
    return (
      <div className="ma-screen is-book">
        <p>Reserve</p>
        <strong>Studio hour</strong>
        <div className="ma-slots"><span>1:00</span><span className="is-on">2:00</span><span>3:30</span></div>
        <button type="button">Confirm</button>
      </div>
    );
  }
  return (
    <div className="ma-screen">
      <p>Today</p>
      <strong>Good morning</strong>
      <div className="ma-card-row"><span /><b>Next session</b></div>
      <ul>
        <li>Messages</li>
        <li>Schedule</li>
      </ul>
    </div>
  );
}

function Phone({ kind, className = "" }) {
  return (
    <div className={`ma-phone ${className}`}>
      <div className="ma-notch" />
      <Screen kind={kind} />
    </div>
  );
}

function HeroPhones() {
  return (
    <div className="ma-hero-phones">
      <Phone kind="account" className="is-back ma-phone-bit" />
      <Phone kind="home" className="is-front ma-phone-bit" />
      <Phone kind="detail" className="is-mid ma-phone-bit" />
    </div>
  );
}

function JourneyMap() {
  const [active, setActive] = useState(0);
  const [ref, shown] = useInView(0.35);
  return (
    <div ref={ref} className={`ma-journey ${shown ? "is-live" : ""}`}>
      <div className="ma-journey-stage">
        <svg className="ma-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {[[50, 12], [16, 34], [84, 34], [18, 78], [82, 78]].map(([x, y], index) => (
            <line key={`d-${x}-${y}`} x1="50" y1="48" x2={x} y2={y} pathLength="1" className={active === index ? "is-hot" : ""} />
          ))}
        </svg>
        <svg className="ma-lines ma-lines-mobile" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {[[50, 10], [20, 30], [80, 30], [22, 78], [78, 78]].map(([x, y], index) => (
            <line key={`m-${x}-${y}`} x1="50" y1="48" x2={x} y2={y} pathLength="1" className={active === index ? "is-hot" : ""} />
          ))}
        </svg>
        <div className="ma-journey-phone"><Phone kind={["home", "home", "detail", "book", "account"][active]} /></div>
        {journey.map(([id, label], index) => (
          <button
            key={id}
            type="button"
            className={`ma-node ${active === index ? "is-hot" : ""}`}
            data-id={id}
            onMouseEnter={() => setActive(index)}
            onFocus={() => setActive(index)}
            onClick={() => setActive(index)}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="ma-caption">{journey[active][2]}</p>
    </div>
  );
}

const focusLayout = [
  ["nav", 50, 10],
  ["touch", 14, 32],
  ["content", 86, 34],
  ["actions", 16, 80],
  ["notes", 84, 78]
];

const focusLayoutMobile = [
  ["nav", 50, 8],
  ["touch", 18, 30],
  ["content", 82, 30],
  ["actions", 22, 92],
  ["notes", 78, 92]
];

function FocusPhone() {
  const [zone, setZone] = useState("actions");
  const [ref, shown] = useInView(0.3);
  const note = focusPoints.find(([id]) => id === zone);
  return (
    <div ref={ref} className={`ma-focus ${shown ? "is-live" : ""}`}>
      <div className="ma-focus-stage">
        <svg className="ma-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {focusLayout.map(([id, x, y]) => (
            <line key={id} x1="50" y1="48" x2={x} y2={y} pathLength="1" className={zone === id ? "is-hot" : ""} />
          ))}
        </svg>
        <svg className="ma-lines ma-lines-mobile" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {focusLayoutMobile.map(([id, x, y]) => (
            <line key={id} x1="50" y1="46" x2={x} y2={y} pathLength="1" className={zone === id ? "is-hot" : ""} />
          ))}
        </svg>
        <div className={`ma-focus-phone is-${zone}`}>
          <div className="ma-notch" />
          <div className="ma-zone is-nav"><span>Today</span><i className="is-notes" /></div>
          <div className="ma-zone is-touch"><b>Next session</b><em>Thu · 2:00</em></div>
          <div className="ma-zone is-content"><span>Messages</span><span>Schedule</span></div>
          <div className="ma-zone is-actions"><button type="button">Reserve</button></div>
        </div>
        {focusPoints.map(([id, label]) => {
          const spot = focusLayout.find(([key]) => key === id);
          return (
            <button
              key={id}
              type="button"
              data-id={id}
              className={`ma-node ${zone === id ? "is-hot" : ""}`}
              style={{ "--x": `${spot[1]}%`, "--y": `${spot[2]}%` }}
              onMouseEnter={() => setZone(id)}
              onFocus={() => setZone(id)}
              onClick={() => setZone(id)}
            >
              {label}
            </button>
          );
        })}
      </div>
      <p className="ma-caption">{note[2]}</p>
    </div>
  );
}

function PlatformSet() {
  const [active, setActive] = useState("ios");
  const [ref, shown] = useInView(0.3);
  return (
    <div ref={ref} className={`ma-platforms ${shown ? "is-live" : ""}`}>
      <div className="ma-platform-core">Your App</div>
      <div className="ma-platform-row">
        {platforms.map(([id, label, text]) => (
          <button
            key={id}
            type="button"
            className={`ma-platform ${active === id ? "is-hot" : ""}`}
            onMouseEnter={() => setActive(id)}
            onFocus={() => setActive(id)}
            onClick={() => setActive(id)}
          >
            <span>{label}</span>
            <Phone kind="home" />
            <em>{text}</em>
          </button>
        ))}
      </div>
    </div>
  );
}

function AppHub() {
  const [active, setActive] = useState(null);
  const [ref, shown] = useInView(0.35);
  return (
    <div ref={ref} className={`ma-hub ${shown ? "is-live" : ""} ${active ? "is-hot" : ""}`}>
      <div className="ma-hub-stage">
        <svg className="ma-hub-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {hubNodes.map(([id, , x, y], index) => (
            <line key={id} x1="50" y1="48" x2={parseFloat(x)} y2={parseFloat(y)} pathLength="1" className={active === id ? "is-hot" : ""} style={{ "--d": `${160 + index * 60}ms` }} />
          ))}
        </svg>
        <svg className="ma-hub-lines ma-hub-lines-mobile" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {hubMobile.map(([id, x, y], index) => (
            <line key={id} x1="50" y1="44" x2={x} y2={y} pathLength="1" className={active === id ? "is-hot" : ""} style={{ "--d": `${160 + index * 60}ms` }} />
          ))}
        </svg>
        <div className="ma-hub-core">
          <Glyph type="phone" />
          <strong>Mobile App</strong>
          <em>Product</em>
        </div>
        {hubNodes.map(([id, label, x, y], index) => (
          <button
            key={id}
            type="button"
            data-id={id}
            className={`ma-hub-node ${active === id ? "is-hot" : ""}`}
            style={{ "--x": x, "--y": y, "--n": `${380 + index * 50}ms` }}
            onMouseEnter={() => setActive(id)}
            onMouseLeave={() => setActive(null)}
            onFocus={() => setActive(id)}
            onBlur={() => setActive(null)}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="ma-caption">
        {active ? `${hubNodes.find(([id]) => id === active)[1]} connects when that system allows it.` : "The app at the center. Services connected around it."}
      </p>
    </div>
  );
}

function ScreenRow() {
  const [ref, shown] = useInView(0.25);
  const screens = [["home", "Home"], ["detail", "Details"], ["book", "Action"], ["account", "Account"]];
  return (
    <div ref={ref} className={`ma-screens ${shown ? "is-live" : ""}`}>
      {screens.map(([kind, label], index) => (
        <figure key={label} style={{ "--i": index }}>
          <Phone kind={kind} />
          <figcaption>{label}</figcaption>
        </figure>
      ))}
    </div>
  );
}

function ProcessViz({ index }) {
  if (index === 0) {
    return <div className="ma-viz ma-viz-net">{["User", "Business", "Goals", "Requirements"].map((label) => <span key={label}>{label}</span>)}</div>;
  }
  if (index === 1) {
    return (
      <div className="ma-viz ma-viz-flow">
        {["Features", "User flows", "Architecture"].map((label, step) => (
          <React.Fragment key={label}>{step > 0 ? <i aria-hidden="true" /> : null}<span>{label}</span></React.Fragment>
        ))}
      </div>
    );
  }
  if (index === 2) {
    return <ul className="ma-viz ma-viz-layers">{["Wireframe", "UI", "Prototype"].map((label, step) => <li key={label}><em>{String(step + 1).padStart(2, "0")}</em><span>{label}</span></li>)}</ul>;
  }
  if (index === 3) {
    return <ul className="ma-viz ma-viz-layers">{["Mobile UI", "Code", "API", "Backend"].map((label, step) => <li key={label}><em>{String(step + 1).padStart(2, "0")}</em><span>{label}</span></li>)}</ul>;
  }
  if (index === 4) {
    return <ul className="ma-viz ma-viz-checks">{["Devices", "Interactions", "Forms", "Performance"].map((label) => <li key={label}><i aria-hidden="true" />{label}</li>)}</ul>;
  }
  if (index === 5) {
    return (
      <div className="ma-viz ma-viz-launch">
        <div><small>App</small><strong>Prepared</strong></div>
        <span>Release</span>
        <div className="is-live"><small>App</small><strong>Production</strong></div>
      </div>
    );
  }
  return (
    <div className="ma-viz ma-viz-flow">
      {["Monitor", "Learn", "Improve"].map((label, step) => (
        <React.Fragment key={label}>{step > 0 ? <i aria-hidden="true" /> : null}<span>{label}</span></React.Fragment>
      ))}
    </div>
  );
}

function ProcessTimeline() {
  const [active, setActive] = useState(3);
  const refs = useRef([]);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let observer;
    const bind = () => {
      observer?.disconnect();
      if (desktop.matches || reduced.matches) return;
      const nodes = refs.current.filter(Boolean);
      observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(Number(visible.target.dataset.index));
      }, { threshold: [0.6], rootMargin: "-30% 0px -40% 0px" });
      nodes.forEach((node) => observer.observe(node));
    };
    bind();
    desktop.addEventListener("change", bind);
    return () => {
      desktop.removeEventListener("change", bind);
      observer?.disconnect();
    };
  }, []);
  const [title, text] = process[active];
  return (
    <>
      <div className="ma-process" style={{ "--ma-fill": `${(active / (process.length - 1)) * 100}%` }}>
        <div className="ma-process-rail" />
        <div className="ma-process-fill" />
        {process.map(([stepTitle, stepText], index) => (
          <button
            key={stepTitle}
            type="button"
            data-index={index}
            ref={(node) => { refs.current[index] = node; }}
            className={`ma-step ${active === index ? "is-active" : ""}`}
            aria-pressed={active === index}
            onClick={() => setActive(index)}
          >
            <span className="ma-step-mark">{String(index + 1).padStart(2, "0")}</span>
            <span className="ma-step-copy">
              <span>{stepTitle}</span>
              <span>{stepText}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="ma-build-stage" aria-live="polite">
        <div className="ma-build-stage-body" key={active}>
          <p className="ma-build-stage-index">{String(active + 1).padStart(2, "0")}</p>
          <h3>{title}</h3>
          <p className="ma-build-stage-copy">{text}</p>
          <ProcessViz index={active} />
        </div>
      </div>
    </>
  );
}

function FaqList() {
  const [open, setOpen] = useState(null);
  return (
    <div className="border-t border-slate-200">
      {faqs.map(([question, answer], index) => {
        const isOpen = open === index;
        return (
          <div key={question} className={`ma-acc border-b border-slate-200 ${isOpen ? "is-open" : ""}`}>
            <button type="button" className="w-full flex items-center justify-between gap-6 py-3.5 text-left" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : index)}>
              <span className="font-semibold text-slate-900 font-heading">{question}</span>
              <span className="ma-plus" aria-hidden="true"><i /><i /></span>
            </button>
            <div className={`ma-acc-panel ${isOpen ? "is-open" : ""}`}>
              <div><p className="pb-5 max-w-3xl text-slate-600 leading-relaxed">{answer}</p></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function MobileDevelopment() {
  useEffect(() => {
    const previous = document.title;
    document.title = "Mobile App Development | BizLogic Developers";
    return () => { document.title = previous; };
  }, []);

  return (
    <article data-testid="service-detail-page" className="ma-page bg-white">
      <header className="ma-hero border-b border-slate-200">
        <div className="ma-wrap grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-6">
            <Link to="/services" data-testid="service-detail-back" className="ma-hero-item inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 mb-5" style={{ animationDelay: "40ms" }}>
              <ArrowLeft className="w-4 h-4" />
              All services
            </Link>
            <p className="ma-hero-item text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 font-heading" style={{ animationDelay: "120ms" }}>
              Mobile app development
            </p>
            <h1 className="ma-hero-item text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-slate-900 font-heading leading-[1.05]" style={{ animationDelay: "220ms" }}>
              Apps Built for<span className="hidden xl:inline"><br /></span> People on the Move.
            </h1>
            <p className="ma-hero-item ma-lead max-w-xl text-lg text-slate-600 leading-relaxed" style={{ animationDelay: "360ms" }}>
              Mobile experiences designed around your users, your business, and the way people actually use your product.
            </p>
            <div className="ma-hero-item ma-after flex flex-col sm:flex-row gap-3" style={{ animationDelay: "500ms" }}>
              <Link to={CONTACT} data-testid="service-panel-cta-mobile-development" className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3.5 rounded-xl shadow-md shadow-blue-600/10 transition-all hover:-translate-y-0.5">
                Start a Project
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/#portfolio-section" className="group inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-blue-600 text-slate-800 hover:text-blue-600 font-medium px-6 py-3.5 rounded-xl transition-colors">
                View Our Work
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <HeroPhones />
          </div>
        </div>
      </header>

      <section className="ma-section border-b border-slate-200">
        <div className="ma-wrap">
          <Reveal className="max-w-2xl">
            <Kicker index="01" label="The mobile experience" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Your Product, in Their Pocket.</h2>
            <p className="ma-lead text-slate-600 leading-relaxed">
              A mobile app is not a smaller website. People use it with a thumb, in short moments, and they come back when the product gives them a reason. Navigation, speed, and the device itself shape the experience.
            </p>
          </Reveal>
          <div className="ma-after"><JourneyMap /></div>
        </div>
      </section>

      <section className="ma-section border-b border-slate-200 bg-slate-50/70">
        <div className="ma-wrap">
          <Reveal>
            <Kicker index="02" label="What we build" />
            <h2 className="max-w-2xl text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">From Simple Tools to Full Mobile Products.</h2>
          </Reveal>
          <div className="ma-after ma-build-grid">
            {builds.map(([number, glyph, title, text], index) => (
              <Reveal key={title} delay={(index % 2) * 60} className="h-full">
                <article className="ma-card">
                  <span className="ma-card-mark"><Glyph type={glyph} /></span>
                  <span className="ma-card-index">{number}</span>
                  <ArrowRight className="ma-arrow w-4 h-4 text-slate-400" />
                  <h3 className="text-lg font-semibold text-slate-900 font-heading">{title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="ma-section border-b border-slate-200">
        <div className="ma-wrap">
          <Reveal className="max-w-2xl">
            <Kicker index="03" label="App experience" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Designed Around the Way People Use Their Phones.</h2>
            <p className="ma-lead text-slate-600 leading-relaxed">
              The interface follows the hand. Navigation stays reachable, content stays close to the decision, and the next action is obvious.
            </p>
          </Reveal>
          <div className="ma-after"><FocusPhone /></div>
        </div>
      </section>

      <section className="ma-section border-b border-slate-200 bg-[#f7f9fc]">
        <div className="ma-wrap">
          <Reveal className="max-w-2xl">
            <Kicker index="04" label="Platforms" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Built for the Platforms Your Users Already Use.</h2>
            <p className="ma-lead text-slate-600 leading-relaxed">
              The product can live on iOS, Android, or both. The screens stay recognizable. The platform choice follows where the users are.
            </p>
          </Reveal>
          <div className="ma-after"><PlatformSet /></div>
        </div>
      </section>

      <section className="ma-section border-b border-slate-200">
        <div className="ma-wrap grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="lg:order-2">
            <Reveal>
              <Kicker index="05" label="Connected applications" />
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">The App Is Only Part of the Product.</h2>
              <p className="ma-lead text-slate-600 leading-relaxed">
                A mobile app usually talks to something behind it: an API, sign-in, stored data, notifications, or another system the business already runs. The connection depends on what that system allows.
              </p>
            </Reveal>
          </div>
          <AppHub />
        </div>
      </section>

      <section className="ma-section ma-build border-b border-slate-200">
        <div className="ma-wrap">
          <div className="ma-build-intro">
            <Reveal className="max-w-2xl">
              <Kicker index="06" label="How we build" />
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">From Idea to App Store.</h2>
              <p className="ma-lead text-slate-600 leading-relaxed">
                Select a step. The active stage is the work in front of you, from the first conversation through improvement after release.
              </p>
            </Reveal>
          </div>
          <ProcessTimeline />
        </div>
      </section>

      <section className="ma-section border-b border-slate-200">
        <div className="ma-wrap">
          <Reveal className="max-w-2xl">
            <Kicker index="07" label="App interface" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Every Screen Has a Purpose.</h2>
            <p className="ma-lead text-slate-600 leading-relaxed">
              Home, detail, the action, and the account should feel like one product. The type, the controls, and the navigation stay consistent from screen to screen.
            </p>
          </Reveal>
          <div className="ma-after"><ScreenRow /></div>
        </div>
      </section>

      <section className="ma-section border-b border-slate-200">
        <div className="ma-wrap">
          <Reveal>
            <Kicker index="08" label="Why BizLogic" />
            <h2 className="max-w-3xl text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Build the Product, Not Just the App.</h2>
          </Reveal>
          <div className="ma-after grid grid-cols-1 md:grid-cols-2 gap-3">
            {principles.map(([title, text], index) => {
              const wide = index === 0 || index === principles.length - 1;
              return (
                <Reveal key={title} delay={(index % 2) * 80} className={wide ? "md:col-span-2" : ""}>
                  <article className={`h-full border border-slate-200 bg-white p-5 sm:p-6 ${wide ? "md:grid md:grid-cols-2 md:gap-8 md:items-center" : ""}`}>
                    <h3 className="text-2xl font-semibold text-slate-900 font-heading">{title}</h3>
                    <p className={`mt-3 text-slate-600 leading-relaxed ${wide ? "md:mt-0" : ""}`}>{text}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="ma-section border-b border-slate-200">
        <div className="ma-wrap">
          <Reveal>
            <Kicker index="09" label="FAQ" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Questions about a mobile product.</h2>
          </Reveal>
          <div className="ma-after"><FaqList /></div>
        </div>
      </section>

      <section className="ma-cta">
        <div className="ma-cta-grid" />
        <div className="ma-cta-glow" />
        <div className="ma-wrap relative ma-section">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight font-heading leading-tight">
            Let&apos;s Turn the Idea Into a Real Product.
          </h2>
          <p className="ma-lead text-slate-300 leading-relaxed">
            Tell us what you&apos;re trying to build, improve, or solve. We&apos;ll help shape the right approach for your mobile product.
          </p>
          <div className="ma-after flex flex-col sm:flex-row gap-3">
            <Link to={CONTACT} className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5">
              Start a Project
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/services" className="group inline-flex items-center justify-center gap-2 border border-slate-600 hover:border-white text-white font-semibold px-6 py-3.5 rounded-xl transition-colors">
              Explore Services
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
